import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  X,
  ExternalLink,
  Mail,
  Paperclip,
  FileDown,
  RotateCcw,
  Trash2,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { searchModeLabel, type FileMeta, type LogDetail } from "@/lib/types";
import {
  fmtKst,
  fmtMs,
  fmtModel,
  fmtBytes,
  cn,
  inputFiles,
  outputFiles,
  questionText,
} from "@/lib/utils";

interface Props {
  queryId: string | null;
  onClose: () => void;
  /** '삭제된 이력 보기' 중이면 삭제분도 상세 조회한다(백엔드는 기본 404). */
  includeDeleted?: boolean;
  /** 넘기면 상세 패널에 이력 삭제/복원 버튼이 표시된다. */
  onDelete?: (d: LogDetail) => void;
  onRestore?: (d: LogDetail) => void;
  /** 넘기면 '메일 보내기' 버튼이 표시된다(user_id 가 이메일일 때만). */
  onSendMail?: (d: LogDetail) => void;
}

export function LogDetailPanel({
  queryId,
  onClose,
  includeDeleted = false,
  onDelete,
  onRestore,
  onSendMail,
}: Props) {
  const open = queryId !== null;

  const { data, isLoading, isError } = useQuery({
    queryKey: ["log-detail", queryId, includeDeleted],
    queryFn: () =>
      api.get<LogDetail>(
        `/logs/${queryId}`,
        includeDeleted ? { include_deleted: true } : undefined,
      ),
    enabled: open,
  });

  // ESC 닫기
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  return (
    <>
      {/* 배경 오버레이 */}
      <div
        onClick={onClose}
        className={cn(
          "fixed inset-0 z-40 bg-black/30 transition-opacity",
          open ? "opacity-100" : "pointer-events-none opacity-0",
        )}
      />

      {/* 슬라이드 패널 */}
      <aside
        className={cn(
          "fixed inset-y-0 right-0 z-50 w-full max-w-xl border-l bg-background shadow-2xl transition-transform",
          open ? "translate-x-0" : "translate-x-full",
        )}
        aria-hidden={!open}
      >
        <header className="flex items-center justify-between border-b px-5 py-3">
          <h3 className="font-semibold">질의 상세</h3>
          <div className="flex items-center gap-1">
            {data && onSendMail && data.user_id?.includes("@") && (
              <Button
                variant="outline"
                size="sm"
                className="gap-1"
                onClick={() => onSendMail(data)}
              >
                <Mail className="h-4 w-4" />
                메일 보내기
              </Button>
            )}
            {data && onRestore && data.deleted && (
              <Button
                variant="outline"
                size="sm"
                className="gap-1"
                onClick={() => onRestore(data)}
              >
                <RotateCcw className="h-4 w-4" />
                복원
              </Button>
            )}
            {data && onDelete && !data.deleted && (
              <Button
                variant="ghost"
                size="sm"
                className="gap-1 text-muted-foreground hover:text-destructive"
                onClick={() => onDelete(data)}
              >
                <Trash2 className="h-4 w-4" />
                이력 삭제
              </Button>
            )}
            <Button variant="ghost" size="icon" onClick={onClose} aria-label="닫기">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </header>

        <div className="h-[calc(100%-3.25rem)] overflow-y-auto p-5">
          {isLoading && <DetailSkeleton />}
          {isError && (
            <div className="text-sm text-destructive">
              상세 정보를 불러오지 못했습니다.
            </div>
          )}
          {data && <DetailBody d={data} />}
        </div>
      </aside>
    </>
  );
}

function DetailSkeleton() {
  return (
    <div className="space-y-4">
      <Skeleton className="h-5 w-40" />
      <Skeleton className="h-4 w-3/4" />
      <Skeleton className="h-32 w-full" />
      <Skeleton className="h-4 w-1/2" />
      <Skeleton className="h-32 w-full" />
    </div>
  );
}

function DetailBody({ d }: { d: LogDetail }) {
  const attached = inputFiles(d.files);   // 사용자가 보낸 첨부
  const produced = outputFiles(d.files);  // 스킬 산출물
  return (
    <div className="space-y-5">
      <section>
        <Row label="시각" value={fmtKst(d.timestamp)} />
        <Row label="사용자" value={`${d.user_name ?? "-"} (${d.user_id ?? "-"})`} />
        <Row label="응답시간" value={fmtMs(d.response_ms)} />
        <Row
          label="모델"
          value={d.model ? `${fmtModel(d.model)} (${d.model})` : "-"}
        />
        <Row
          label="검색 방법"
          value={d.search_mode ? `${searchModeLabel(d.search_mode)} (${d.search_mode})` : "-"}
        />
        <Row label="query_id" value={d.query_id} mono />
        <div className="mt-2 flex gap-2">
          {d.was_answered ? (
            <Badge variant="success">답변</Badge>
          ) : (
            <Badge variant="destructive">미답변</Badge>
          )}
          {d.deleted && <Badge variant="secondary">삭제됨(숨김)</Badge>}
          {d.was_saved && <Badge variant="secondary">KMS 저장</Badge>}
          {d.feedback === 1 && <Badge variant="success">👍</Badge>}
          {d.feedback === -1 && <Badge variant="destructive">👎</Badge>}
        </div>
      </section>

      <section>
        <h4 className="mb-1 text-xs font-semibold text-muted-foreground">요청 내용</h4>
        <pre className="whitespace-pre-wrap rounded-md border bg-muted/30 p-3 text-sm">
          {questionText(d.question, d.files)}
        </pre>
      </section>

      {attached.length > 0 && (
        <section>
          <h4 className="mb-1 text-xs font-semibold text-muted-foreground">
            사용자가 보낸 첨부 ({attached.length}건)
          </h4>
          <ul className="space-y-1">
            {attached.map((f, i) => (
              <FileRow key={`in-${i}-${f.name}`} f={f} icon="clip" />
            ))}
          </ul>
        </section>
      )}

      {produced.length > 0 && (
        <section>
          <h4 className="mb-1 text-xs font-semibold text-muted-foreground">
            산출물 ({produced.length}건)
          </h4>
          <ul className="space-y-1">
            {produced.map((f, i) => (
              <FileRow key={`out-${i}-${f.name}`} f={f} icon="down" />
            ))}
          </ul>
        </section>
      )}

      <section>
        <h4 className="mb-1 text-xs font-semibold text-muted-foreground">답변</h4>
        <pre className="whitespace-pre-wrap rounded-md border bg-muted/30 p-3 text-sm">
          {d.answer || "(미답변)"}
        </pre>
      </section>

      <section className="text-xs text-muted-foreground">
        <a
          href="https://arkivekb.pilgrimscenter.org"
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-primary hover:underline"
        >
          BookStack 에서 관련 문서 검색
          <ExternalLink className="h-3 w-3" />
        </a>
      </section>
    </div>
  );
}

function FileRow({ f, icon }: { f: FileMeta; icon: "clip" | "down" }) {
  const size = fmtBytes(f.size);
  return (
    <li className="flex items-center gap-2 rounded-md border bg-muted/20 px-2.5 py-1.5 text-sm">
      {icon === "clip" ? (
        <Paperclip className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      ) : (
        <FileDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      )}
      <span className="min-w-0 flex-1 break-all">{f.name}</span>
      {size && <span className="shrink-0 text-xs text-muted-foreground">{size}</span>}
    </li>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex gap-3 py-1 text-sm">
      <span className="w-20 shrink-0 text-muted-foreground">{label}</span>
      <span className={cn("min-w-0 break-all", mono && "font-mono text-xs")}>{value}</span>
    </div>
  );
}
